import { ReactNode } from 'react';
import { useCurrentFrame, useVideoConfig, staticFile } from 'remotion';

// Components
import { FallbackRemotionImg } from '@/components/common/FallbackRemotionImg';
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';
import { TypingText } from '@/components/common/animations/TypingText';

// Types
import { TrainingFeedbackCardProps } from '../types';

// Constants
import { LAYOUT_CONFIG, TIMING } from '../constants';

// Utils
import { combineClasses, processImageUrlForRemotion } from '@/utils';
import {
  getContainerEntranceAnimation,
  getProfileAnimation,
  getNameTitleAnimation,
  getQuotationAnimation,
  getTopicLabelAnimation,
  getExitOpacity,
} from '../utils';

// Icons
import { QuotationMarksIcon } from '@shared/icons/QuotationMarksIcon';

// Themes
import { lalezarFontFamily } from '@/styles/fonts';

export const TrainingFeedbackCard = ({
  relatedAvatar,
  relatedName,
  role,
  feedbackHighlight,
  topicLabel,
  variant,
  startFrame,
  transitionStartFrame,
}: TrainingFeedbackCardProps): ReactNode => {
  const { typography, colors, rawColors } = employeeYearInReviewTheme;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const config = LAYOUT_CONFIG[variant];

  const localFrame = frame - startFrame;

  // Normalize avatar URL (remove Google Image size params, validate format)
  const processedAvatarUrl = processImageUrlForRemotion(
    relatedAvatar,
    staticFile,
  );

  // Calculate exit animation progress
  const isExiting =
    transitionStartFrame !== undefined && frame >= transitionStartFrame;
  const exitFrame = isExiting ? frame - transitionStartFrame : 0;
  const exitProgress = isExiting
    ? Math.min(exitFrame / TIMING.TRANSITION_DURATION, 1)
    : 0;

  // Container entrance animation (overall card fade-in + scale)
  const containerAnimation = getContainerEntranceAnimation({
    localFrame,
    fps,
    isExiting,
    exitProgress,
  });

  // Profile image animation
  const profileAnimation = getProfileAnimation({
    localFrame,
    fps,
    variant,
    isExiting,
    exitProgress,
  });

  // Name animation
  const nameAnimation = getNameTitleAnimation({
    localFrame,
    fps,
    isExiting,
    exitProgress,
    isTitle: false,
  });

  // Title animation
  const titleAnimation = getNameTitleAnimation({
    localFrame,
    fps,
    isExiting,
    exitProgress,
    isTitle: true,
  });

  // Quotation marks animation
  const quotationAnimation = getQuotationAnimation({
    localFrame,
    fps,
    variant,
    isExiting,
    exitProgress,
  });

  // Topic label animation
  const topicAnimation = getTopicLabelAnimation({
    localFrame,
    fps,
    feedbackLength: feedbackHighlight?.length || 0,
    isExiting,
    exitProgress,
  });

  // Typing text opacity for exit transition
  const typingTextOpacity = isExiting ? getExitOpacity(exitProgress) : 1;

  const renderProfileImage = (): React.JSX.Element | null => {
    if (!processedAvatarUrl) return null;

    return (
      <div
        className={combineClasses(
          'relative shrink-0 self-start rounded-full overflow-hidden',
          'shadow-2xl border-solid bg-slate-700',
          colors.borderSecondary,
        )}
        style={{
          width: config.profileSize,
          height: config.profileSize,
          borderWidth: config.profileBorderWidth,
          opacity: profileAnimation.opacity,
          transform: `translateX(${profileAnimation.translateX}px)`,
        }}
      >
        <FallbackRemotionImg
          src={processedAvatarUrl}
          className="size-full object-cover"
        />
      </div>
    );
  };

  const renderContent = (): React.JSX.Element => (
    <div
      className="flex flex-col gap-4 flex-1 mt-24"
      style={{
        alignItems: config.contentAlign === 'right' ? 'flex-end' : 'flex-start',
        textAlign: config.contentAlign,
      }}
    >
      {/* Name */}
      <p
        className={combineClasses(typography.lg, colors.textPrimary)}
        style={{
          opacity: nameAnimation.opacity,
          transform: `scale(${nameAnimation.scale})`,
        }}
      >
        {relatedName}
      </p>

      {/* Title */}
      <p
        className={combineClasses(
          typography.md,
          colors.textSecondary,
          'tracking-widest',
        )}
        style={{
          fontFamily: lalezarFontFamily,
          opacity: titleAnimation.opacity,
          transform: `scale(${titleAnimation.scale})`,
        }}
      >
        {role}
      </p>

      {/* Quote */}
      <div
        className={combineClasses(
          'min-h-[240px] max-w-xl mt-7',
          config.contentAlign === 'right' ? 'text-right' : 'text-left',
        )}
        style={{
          opacity: typingTextOpacity,
        }}
      >
        <TypingText
          text={feedbackHighlight || ''}
          startFrame={startFrame + TIMING.QUOTE.startFrame}
          charsPerFrame={TIMING.QUOTE.charsPerFrame}
          showCursor={true}
          cursorColor={rawColors.cursor}
          className={combineClasses(
            typography.xs,
            'text-frost-100 mt-4 font-semibold leading-normal',
          )}
          style={{
            fontFamily: 'Inter, sans-serif',
            textAlign: config.contentAlign,
          }}
        />
      </div>

      {/* Topic Label */}
      <p
        className={combineClasses(
          'mt-10',
          typography['2xs'],
          colors.textSecondary,
          'max-w-xl',
        )}
        style={{
          opacity: topicAnimation.opacity,
          transform: `translateX(${topicAnimation.translateX}px)`,
          fontStyle: variant === 'avatar-right' ? 'italic' : 'normal',
        }}
      >
        - {topicLabel}
      </p>
    </div>
  );

  const renderQuotationMarks = (): React.JSX.Element => {
    if (config.quotationPosition === 'bottom-right') {
      return (
        <QuotationMarksIcon
          className={combineClasses(
            'absolute bottom-0 right-0 mr-[60px] mb-[55px] origin-center',
            colors.secondaryFillIcon,
          )}
          style={{
            transform: `scale(${quotationAnimation.scale}) rotate(${quotationAnimation.rotate}deg)`,
          }}
        />
      );
    }

    // Avatar-right: quotation marks on the left
    return (
      <QuotationMarksIcon
        className={combineClasses(
          'absolute bottom-0 left-0 ml-[70px] mb-[55px] origin-center',
          colors.secondaryFillIcon,
        )}
        style={{
          transform: `scale(${quotationAnimation.scale}) rotate(${quotationAnimation.rotate}deg) scaleX(-1)`,
        }}
      />
    );
  };

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{
        opacity: containerAnimation.opacity,
        transform: `scale(${containerAnimation.scale}) translateY(${containerAnimation.translateY}px)`,
      }}
    >
      <div className="flex items-center gap-14 px-24">
        {/* Avatar Left Layout */}
        {variant === 'avatar-left' && (
          <>
            {renderProfileImage()}
            {renderContent()}
          </>
        )}

        {/* Avatar Right Layout */}
        {variant === 'avatar-right' && (
          <>
            {renderQuotationMarks()}
            {renderContent()}
            {renderProfileImage()}
          </>
        )}
      </div>

      {/* Quotation marks for avatar-left variant */}
      {variant === 'avatar-left' && renderQuotationMarks()}
    </div>
  );
};
